import { useState } from 'react';
import { Lock, Check, AlertCircle, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleChange = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPwd !== confirm) {
      setError('New passwords do not match');
      return;
    }
    if (newPwd.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: newPwd }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(data.message || 'Password changed successfully');
        setCurrent('');
        setNewPwd('');
        setConfirm('');
      } else {
        setError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setError('Network error');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Admin Settings</h1>

      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6 max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-semibold text-white">Change Admin Password</h2>
        </div>

        <form onSubmit={handleChange} className="space-y-4">
          <div>
            <Label className="text-white/60 text-sm">Current Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="Enter current password"
                className="pl-10 bg-white/5 border-white/10 text-white"
                required
                data-testid="admin-current-password"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-sm">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min 8 characters"
                className="pl-10 bg-white/5 border-white/10 text-white"
                required
                minLength={8}
                data-testid="admin-new-password"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-sm">Confirm New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat new password"
                className="pl-10 bg-white/5 border-white/10 text-white"
                required
                data-testid="admin-confirm-password"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {message && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <Check className="w-4 h-4" /> {message}
            </div>
          )}

          <Button
            type="submit"
            disabled={saving || !current || !newPwd || !confirm}
            className="w-full shimmer-btn text-white rounded-lg border-0"
            data-testid="admin-change-password-btn"
          >
            {saving ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
